import React, { useEffect, useState } from "react";
import { CoherenceDataGrid } from "@coherence-design-system/data-grid";
import { Stack, Selection, SearchBox, SelectionMode, MessageBar, MessageBarType, TextField } from "@fluentui/react";
import PaginationComponent from "../Quotation/Components/PaginationComponent";
import {
  DefaultCurrentPage,
  DefaultCurrentPageSize,
  DefaultTotalPages,
  NO,
  YES,
} from "../Quotation/Common/QuotationData";
import {
  ISlotDetail,
  IStandaloneDevice,
} from "../../common/Interfaces/ISearchSkuResponse";
import { DeviceType } from "../../common/enum/DeviceTypes";
import { IExtendedSlotDetail } from "../../common/Interfaces/IBundleProduct";
import { CustomSpanStyle } from "../Quotation/QuotationStyle";
import { priceWithCurrency } from "../../Utils/PriceWithCurrency";
import { useBundleItemSelectedKey } from "./BundleItelSelectedKeyContext";

export interface IItemWithQuantity extends IStandaloneDevice {
  quantity?: number;
}

export interface IWizardDesignProps {
  onSubmit: (data: ISlotDetail[], quantities?: Map<string, number>) => void;
  items: ISlotDetail[] | IExtendedSlotDetail[] | IStandaloneDevice[];
  selectedItems: ISlotDetail[] | IExtendedSlotDetail[] | IStandaloneDevice[];
  selectionMode: number;
  deviceType: string;
  onQuantityChange?: (sapItemId: string, quantity: number) => void;
  initialQuantities?: Map<string, number>;
}

const WizardDesign = (props: IWizardDesignProps) => {
  const { items, selectedItems, selectionMode, deviceType, onQuantityChange, initialQuantities } = props;
  const [totalPages, setTotalPages] = useState<number>(DefaultTotalPages);
  const [currentPage, setCurrentPage] = useState<number>(DefaultCurrentPage);
  const [currentPageSize, setCurrentPageSize] = useState<number>(
    DefaultCurrentPageSize
  );

  const [data, setData] = useState<ISlotDetail[] | IStandaloneDevice[]>(items);
  const [modifedData, setModifedData] = useState<
    ISlotDetail[] | IStandaloneDevice[]
  >([]);
  const [selectedGridData, setSelectedGridData] = useState({ items: [] });
  const [searchingItem, setSearchingItem] = useState<string>();
  const [quantities, setQuantities] = useState<Map<string, number>>(new Map());

  const { bundleItemSelectedKey} = useBundleItemSelectedKey();
 
  const _selectoinBundle = new Selection({
    onSelectionChanged: () => {
      const selectedData = _selectoinBundle.getSelection() as IExtendedSlotDetail[];
      props.onSubmit(selectedData, quantities);
    },
  });

  const handleQuantityChange = (sapItemId: string, value: string) => {
    const quantity = Math.max(1, parseInt(value) || 1); // Ensure minimum quantity of 1
    setQuantities(prev => {
      const newMap = new Map(prev);
      newMap.set(sapItemId, quantity);
      return newMap;
    });
    if (onQuantityChange) {
      onQuantityChange(sapItemId, quantity);
    }
  };

  useEffect(() => {
    deviceType === DeviceType.Device && setData(items);
    // Initialize quantities for all items, using initialQuantities from props if available
    const newQuantities = new Map<string, number>();
    items.forEach((item) => {
      // Use existing quantity from initialQuantities, or default to 1
      const existingQty = initialQuantities?.get(item.sapItemId) || 1;
      newQuantities.set(item.sapItemId, existingQty);
    });
    setQuantities(newQuantities);
    
    if (selectedItems?.length) {
      setSelectedGridData({ items: selectedItems as any });
      selectedItems.forEach((item) => {
        const findIndex = items.findIndex(
          (i) => i.sapItemId === item.sapItemId
        );
        _selectoinBundle.setIndexSelected(findIndex, true, true);
      });
    }
  }, [JSON.stringify(items)]);

  useEffect(() => {
    if (searchingItem) {
      const filteredData: ISlotDetail[] | IStandaloneDevice[] = data.filter(
        (item: ISlotDetail | IStandaloneDevice) =>
          item.sapItemId.toLowerCase().includes(searchingItem.toLowerCase()) ||
          item.productTitle.toLowerCase().includes(searchingItem.toLowerCase())
      );
      setTotalPages(Math.ceil(filteredData.length / currentPageSize));
      const paginatedFilteredData = filteredData.slice(
        (currentPage - 1) * currentPageSize,
        currentPage * currentPageSize
      );
      setModifedData(paginatedFilteredData);
    } else {
      setTotalPages(Math.ceil(data.length / currentPageSize));
      const mdata = data.slice(
        (currentPage - 1) * currentPageSize,
        currentPage * currentPageSize
      );
      setModifedData(mdata as []);
    }
  }, [data, searchingItem, currentPage, currentPageSize, totalPages]);

  const renderCustomRow = (props: any, defaultRender: any) => {
    const { sapItemId,availableInventory, isMPriceCalculated } = props.item;
    const rowText =
      (sapItemId === "" || sapItemId === undefined || sapItemId === null)
        ? `Alert: SAP Id is missing.`
        : (availableInventory==0)?"Alert: Are you sure you want to add an item to the cart with no inventory available?":"";
    const warningMessage = !isMPriceCalculated ? `MSRP Ex ${props.item.taxType} not available for this item.` : '';
    const rowAriaLabel = [rowText, warningMessage].filter(Boolean).join('. ') || undefined;
    return (
      <Stack role="presentation">
        {rowText && (
          <div aria-hidden="true">
            <MessageBar messageBarType={MessageBarType.info} className="waringMessage">
              {rowText}
            </MessageBar>
          </div>
        )}       
        {warningMessage && (
          <div aria-hidden="true">
            <MessageBar messageBarType={MessageBarType.warning}>
              {warningMessage}
            </MessageBar>
          </div>
        )}
        {defaultRender({
          ...props,
          "aria-label": rowAriaLabel,
        })}
      </Stack>
    );
  };

  useEffect(()=>{
    bundleItemSelectedKey?.flatMap((subArray) => subArray).forEach((value) => { _selectoinBundle.setKeySelected(value, true, true);});
  })

  return (
    <div
      style={
        selectionMode === SelectionMode.none
          ? { marginLeft: "10%", marginRight: "10%" }
          : {}
      }
    >
      {selectionMode === SelectionMode.none ? (
        <Stack
          horizontal
          horizontalAlign="space-between"
          style={{ alignItems: "center" }}
        >
          {deviceType === DeviceType.Bundle ? (
            <h3 className="contentTitle-441">Bundle Summary</h3>
          ) : (
            <h3 className="contentTitle-441">Non-Bundle Summary</h3>
          )}
          <SearchBox
            placeholder="Search"
            style={{ width: "300px", paddingLeft: "25px" }}
            onChange={(_, newValue) => {
              setSearchingItem(newValue ?? "");
            }}
          />
        </Stack>
      ) : (
        <Stack horizontal horizontalAlign="end">
          <SearchBox
            placeholder="Search"
            style={{ width: "300px", paddingLeft: "25px" }}
            onChange={(_, newValue) => {
              setSearchingItem(newValue ?? "");
            }}
          />
        </Stack>
      )}
      <Stack style={{ height: "500px" }}>
        {modifedData?.length === 0 && (
          <Stack horizontalAlign="center" style={{ marginTop: "10vw" }}>
            <h4>No Record Found</h4>
          </Stack>
        )}
        {modifedData.length > 0 && (
          <CoherenceDataGrid
            ariaLabelForSelectAllCheckbox="select all checkbox"
            checkButtonAriaLabel="checkbox"
            compactMode="compact"
            data={modifedData}
            onRenderRow={renderCustomRow}
            isScrollable
            isSortable
            listConfig={[
              {
                fieldName: "sapItemId",
                key: "sku",
                name: "SKU",
                type: "string",
                isResizable: true,
                maxWidth: 80,
              },
              {
                fieldName: "productTitle",
                key: "productName",
                name: "Product Name",
                type: "string",
                isResizable: true,
                isMultiline: true,
              },
              {
                fieldName: "availableInventory",
                key: "availableInventory",
                name: "Available Inventory",
                type: "number",
                isResizable: true,
              },
              {
                fieldName: undefined,
                key: "quantity",
                name: "Quantity",
                type: "number",
                isResizable: true,
                maxWidth: 80,
                onRender(item) {
                  const currentQty = quantities.get(item.sapItemId) || 1;
                  return (
                    <Stack verticalFill verticalAlign="center" horizontalAlign="end">
                      <TextField
                        type="number"
                        min={1}
                        ariaLabel={`Quantity for ${item.sapItemId}`}
                        value={currentQty.toString()}
                        errorMessage={currentQty <= 0 ? "Invalid Quantity" : ""}
                        onChange={(_, newValue) => {
                          handleQuantityChange(item.sapItemId, newValue || "1");
                        }}
                        styles={{
                          root: { width: "60px" },
                          fieldGroup: { height: "28px", textAlign: "right" },
                          field: { textAlign: "right" }
                        }}
                      />
                    </Stack>
                  );
                },
              },
              {
                fieldName: "msrp",
                key: "MSRP",
                name: modifedData[0].taxType ? `MSRP Ex ${modifedData[0].taxType}` : "MSRP",
                type: "number",
                isResizable: true,
                onRender(item) {
                  return (
                    <Stack verticalFill verticalAlign="center">
                      <span style={CustomSpanStyle}>
                        {priceWithCurrency("", item.msrp)}
                      </span>
                    </Stack>
                  );
                },
              },
              {
                fieldName: "discountedPrice",
                key: "discountedPrice",
                name: "Discounted Price",
                type: "number",
                isResizable: true,
                onRender(item) {
                  return (
                    <Stack verticalFill verticalAlign="center">
                      <span style={CustomSpanStyle}>
                        {priceWithCurrency("", item.discountedPrice)}
                      </span>
                    </Stack>
                  );
                },
              },
              {
                fieldName: "isPreOrder",
                key: "isPreOrder",
                name: "Is Pre Order",
                type: "boolean",
                isResizable: true,
                onRender(item) {
                  return (
                    <Stack verticalFill verticalAlign="center">
                      <span style={CustomSpanStyle}>
                        {item.isPreOrder ? YES : NO}
                      </span>
                    </Stack>
                  );
                },
              },
            ]}
            selection={_selectoinBundle}
            selectionMode={selectionMode}
            selectionPreservedOnEmptyClick
            setKey={`${deviceType}_LIST_DATA_GRID_SORTABLE_SELECTABLE`}
          />
        )}

        {modifedData.length > 0 && (
          <Stack style={{ marginBottom: "25px" }}>
            <PaginationComponent
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              currentPageSize={currentPageSize}
              setCurrentPageSize={setCurrentPageSize}
              totalPages={totalPages}
            />
          </Stack>
        )}
      </Stack>
    </div>
  );
};

export default WizardDesign;
